import React from 'react';
import { motion } from 'framer-motion';
import { Handshake, Star, Building2, Trophy, Mail, Phone, ArrowRight, Sparkles } from 'lucide-react';
import menissez from '../assets/menissez.webp';

// Catégories de partenaires avec placeholders
const PARTNER_CATEGORIES = [
  {
    id: 'majeurs',
    title: 'Partenaires Majeurs',
    subtitle: 'Nos soutiens principaux',
    icon: Star,
    color: '#FFE400',
    partners: [
      { id: 1, name: 'Menissez', logo: menissez, website: '#', description: 'Boulangerie industrielle' },
      { id: 2, name: 'Partenaire 2', logo: null, website: '#', description: 'À venir' },
    ]
  },
  {
    id: 'officiels',
    title: 'Partenaires Officiels',
    subtitle: 'Ils nous accompagnent au quotidien',
    icon: Trophy,
    color: '#BDFF00',
    partners: [
      { id: 3, name: 'Partenaire 3', logo: null, website: '#', description: 'À venir' },
      { id: 4, name: 'Partenaire 4', logo: null, website: '#', description: 'À venir' },
      { id: 5, name: 'Partenaire 5', logo: null, website: '#', description: 'À venir' },
      { id: 6, name: 'Partenaire 6', logo: null, website: '#', description: 'À venir' },
    ]
  },
  {
    id: 'institutionnels',
    title: 'Partenaires Institutionnels',
    subtitle: 'Institutions et collectivités',
    icon: Building2,
    color: '#3B82F6',
    partners: [
      { id: 7, name: 'Ville de Feignies', logo: null, website: '#', description: 'Municipalité' },
      { id: 8, name: 'Ville d\'Aulnoye-Aymeries', logo: null, website: '#', description: 'Municipalité' },
      { id: 9, name: 'Partenaire Institutionnel', logo: null, website: '#', description: 'À venir' },
    ]
  }
];

// Avantages du partenariat
const PARTNERSHIP_BENEFITS = [
  { icon: '📢', title: 'Visibilité', description: 'Logo sur les maillots, panneaux LED et supports de communication' },
  { icon: '🎟️', title: 'Invitations VIP', description: 'Places privilégiées et accès aux événements du club' },
  { icon: '🤝', title: 'Networking', description: 'Rencontres avec d\'autres partenaires et acteurs locaux' },
  { icon: '📱', title: 'Digital', description: 'Présence sur notre site web et réseaux sociaux' },
  { icon: '🎯', title: 'Activation', description: 'Opérations marketing personnalisées les jours de match' },
  { icon: '💼', title: 'RSE', description: 'Association à des valeurs sportives et citoyennes' },
];

const PartnerCard = ({ partner, color }) => (
  <motion.a
    href={partner.website}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ y: -5, scale: 1.02 }}
    className="group relative block"
  >
    <div 
      className="relative h-48 rounded-2xl overflow-hidden transition-all duration-300"
      style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      {/* Glow effect on hover */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"
        style={{ background: `${color}20` }}
      />
      
      {/* Content */}
      <div className="relative h-full flex flex-col items-center justify-center p-6">
        {partner.logo ? (
          <img 
            src={partner.logo} 
            alt={partner.name}
            className="max-h-20 w-auto object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
          />
        ) : (
          <div 
            className="w-20 h-20 rounded-xl flex items-center justify-center text-3xl font-bold"
            style={{ 
              background: `linear-gradient(135deg, ${color}30 0%, ${color}10 100%)`,
              border: `1px dashed ${color}50`
            }}
          >
            {partner.name.charAt(0)}
          </div>
        )}
        
        <p className="mt-4 text-white font-medium text-center">{partner.name}</p>
        <p className="text-gray-500 text-sm">{partner.description}</p>
      </div>
      
      {/* Bottom accent */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
      />
    </div>
  </motion.a>
);

const BenefitCard = ({ benefit, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
    className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-[#FFE400]/30 transition-colors"
  >
    <div className="text-4xl mb-4">{benefit.icon}</div>
    <h4 className="text-white font-bold text-lg mb-2">{benefit.title}</h4>
    <p className="text-gray-400 text-sm">{benefit.description}</p>
  </motion.div>
);

export default function Partenaires() {
  return (
    <div 
      className="min-h-screen"
      style={{ background: 'linear-gradient(180deg, #001B56 0%, #000B24 100%)' }}
    >
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-[#FFE400]/10 blur-3xl" />
          <div className="absolute bottom-20 right-1/4 w-96 h-96 rounded-full bg-[#BDFF00]/10 blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <Handshake className="w-5 h-5 text-[#FFE400]" />
              <span className="text-gray-300 text-sm">Ensemble, construisons l'avenir</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Nos <span className="text-[#FFE400]">Partenaires</span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8">
              Ils nous font confiance et nous accompagnent dans notre montée vers l'excellence. 
              Merci à tous nos partenaires pour leur soutien indéfectible.
            </p>
            
            <motion.a
              href="#devenir-partenaire"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-black font-bold rounded-xl"
            >
              <Sparkles className="w-5 h-5" />
              Devenir partenaire
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Partners Categories */}
      {PARTNER_CATEGORIES.map((category, catIndex) => {
        const IconComponent = category.icon;
        return (
          <section key={category.id} className="py-16 relative">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex items-center gap-4 mb-10"
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: `${category.color}20` }}
                >
                  <IconComponent className="w-6 h-6" style={{ color: category.color }} />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">{category.title}</h2>
                  <p className="text-gray-400">{category.subtitle}</p>
                </div>
              </motion.div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {category.partners.map((partner) => (
                  <PartnerCard key={partner.id} partner={partner} color={category.color} />
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Become Partner Section */}
      <section id="devenir-partenaire" className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FFE400]/5 to-transparent pointer-events-none" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Rejoignez l'aventure <span className="text-[#FFE400]">EFAFC</span>
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Associez votre image à un club ambitieux en pleine ascension. 
              Découvrez les avantages d'un partenariat avec l'EFAFC.
            </p>
          </motion.div>
          
          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {PARTNERSHIP_BENEFITS.map((benefit, index) => (
              <BenefitCard key={index} benefit={benefit} index={index} />
            ))}
          </div>
          
          {/* Contact CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <div 
              className="p-8 rounded-3xl text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(255,228,0,0.1) 0%, rgba(189,255,0,0.05) 100%)',
                border: '1px solid rgba(255,228,0,0.2)',
              }}
            >
              <h3 className="text-2xl font-bold text-white mb-4">Contactez-nous</h3>
              <p className="text-gray-300 mb-6">
                Notre équipe partenariats est à votre disposition pour étudier ensemble 
                les meilleures opportunités de collaboration.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:partenariats@efafc.fr"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FFE400] text-black font-bold rounded-xl hover:bg-[#BDFF00] transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  partenariats@efafc.fr
                </a>
                <a
                  href="tel:+33000000000"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-colors border border-white/20"
                >
                  <Phone className="w-5 h-5" />
                  Nous appeler
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-t border-white/10">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '15+', label: 'Partenaires' },
              { value: '3', label: 'Catégories' },
              { value: '5000+', label: 'Supporters' },
              { value: 'N2', label: 'Division' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-[#FFE400] mb-2">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
